import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Secondary } from "@ui/components/containers/textBlocks";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { RadioButton } from "@ui/components/inputs/radio";
import { Toggle } from "@ui/components/inputs/toggle";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";

interface PublishArticleModalProps extends React.HTMLAttributes<HTMLDivElement> {
	onConfirm: (confirmed: boolean, shouldShare: boolean, shareMessage?: ContentState) => void;
	articleTitle: string;
	articleId?: string;
}
export const PublishArticleModal: React.FC<PublishArticleModalProps> = ({
	articleId,
	articleTitle,
	onConfirm,
	...htmlProps
}) => {
	const [shouldShare, setShouldShare] = useState(!articleId);
	const [shareMessage, setShareMessage] = useState(ContentState.createFromText(""));
	const [authorshipAccepted, setAuthorshipAccepted] = useState(false);

	return (
		<Container
			close={() => {
				onConfirm(false, false);
			}}
			title={I18n.t("article.editor.publish_modal.title")}
			lightTitle
			{...htmlProps}
		>
			<ConfirmTitle>{I18n.t("article.editor.publish_modal.confirm")}</ConfirmTitle>
			<PrimaryMedium>{articleTitle}</PrimaryMedium>
			<Share>
				<Toggle toggled={shouldShare} onChange={setShouldShare} />
				<ShareText>{I18n.t("article.editor.publish_modal.share")}</ShareText>
			</Share>
			{shouldShare && (
				<StyledInput
					mentionsEnabled
					richStyleEnabled={false}
					placeholder={I18n.t("article.editor.publish_modal.share_placeholder")}
					value={shareMessage}
					onChange={(v) => setShareMessage(v)}
				/>
			)}
			<AuthorshipCheckbox
				checkbox
				id={articleTitle}
				name={articleTitle}
				label={I18n.t("article.editor.publish_modal.authorship")}
				checked={authorshipAccepted}
				onChange={() => setAuthorshipAccepted((prev) => !prev)}
			/>
			<PrimaryButton
				onClick={() => onConfirm(true, shouldShare, shareMessage.getPlainText() ? shareMessage : undefined)}
				disabled={!authorshipAccepted}
			>
				{I18n.t("article.editor.publish_modal.submit")}
			</PrimaryButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(20)}
	align-items: center;

	button {
		margin-top: 24px;
	}

	> * {
		max-width: 400px;
		text-align: center;
	}
`;

const ConfirmTitle = styled.div`
	${({ theme }) => theme.textStyles.title};
	margin: 20px 0 35px;
`;

const Share = styled.div`
	margin-top: 34px;
	display: flex;
	align-items: center;
	${gap(12, "row")};

	> :first-child {
		flex-shrink: 0;
	}
`;

const ShareText = styled(Secondary)`
	text-align: initial;
	max-width: 220px;
`;

const StyledInput = styled(RichTextInput)`
	width: 100%;
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 16px;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 10px 12px 9px;
		font-size: 0.8125rem;
	}
	.public-DraftEditorPlaceholder-root {
		font-weight: 400;
		opacity: 0.8;
	}
`;
const AuthorshipCheckbox = styled(RadioButton)`
	margin-top: 20px;
	text-align: left;
`;
